import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from './SEO';

const SHOW_FREE_MONTH = true;

const visualFeatures = [
  { title: 'Dynamic Login Window', img: '/1.Dynamic_login.png' },
  { title: 'Interactive Signup Experience', img: '/2.Dynamic_Signup.png' },
  { title: 'Personalized "My Works" Dashboard', img: '/mywork.png', desc: 'Easily manage all your estimates and revisions in one place.' },
  { title: 'Start a New Estimate Instantly', img: '/3a.Create Estimate.png', desc: 'Begin with minimal input and generate accurate outputs in minutes.' },
  { title: 'Auto-Generate Unique Estimate IDs', img: '/3b.Create Estimate.png', desc: 'Keep every project distinct with automated unique IDs.' },
  { title: 'Manage Multiple Sub-Estimates Seamlessly', img: '/4a. subestimate.png', desc: 'Break complex projects into smaller, manageable sub-estimates.' },
  { title: 'Flexible Measurement Input Options', img: '/4b.subestimate.png', desc: 'Input dimensions with full control — from simple quantities to complex formulas.' },
  { title: 'Review & Compare Reports with Ease', img: '/5a. review multiple reports.png', desc: 'Instantly preview multiple reports before finalizing.' },
  { title: 'Smart Work Duplication', img: '/6.duplicate work.png', desc: 'Quickly clone any previous estimate and adapt it to new projects in seconds.' },
  { title: 'Multi-Revision Support', img: '/7.duplicate revision.png', desc: 'Create multiple versions of an estimate — without losing previous data.' },
  { title: 'Flexible, Affordable Subscription Plans', img: '/subscription.png', desc: 'Enjoy "1 Month FREE", pay-as-you-go credits, or full plans — no renewal hassles, just flexibility.', button: true },
  { title: 'One-Click PDF Exports', img: '/5b. sample pdf report.png', desc: 'Export complete estimate reports as polished PDFs — ready to submit or print.' }
];

const detailedSections = [
  {
    title: 'Estimation Engine',
    points: [
      'Auto-calculates rates based on SSR norms',
      'Handles lead distances, height/floor logic',
      'Copy & update from old estimates easily'
    ]
  },
  {
    title: 'Document Generation',
    points: [
      'Draft Tender Papers',
      'Measurement & Abstract Sheets',
      'Rate Analysis, Royalty, Quality Test Reports'
    ]
  },
  {
    title: 'Smart Inputs',
    points: [
      'Add Non-SSR items with custom rates',
      'Flexible measurement entries (even complex ones)',
      'Import/copy from existing estimates'
    ]
  },
  {
    title: 'Output & Export',
    points: [
      '12+ detailed reports in PDF',
      'On-screen preview and print-ready'
    ]
  }
];

const Features = () => {
  const navigate = useNavigate();
  const ITEMS_PER_LOAD = 8;
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_LOAD);
  const [selectedFeature, setSelectedFeature] = useState(null);

  const handleShowMore = () => {
    setVisibleCount(prev => Math.min(prev + ITEMS_PER_LOAD, visualFeatures.length));
  };

  const currentIndex = selectedFeature
    ? visualFeatures.findIndex(f => f.title === selectedFeature.title)
    : -1;

  const showPrev = currentIndex > 0;
  const showNext = currentIndex < visualFeatures.length - 1;

  return (
    <div className="bg-gray-50 relative">
      <SEO
        title="Features | myBOQ Civil Estimation Tool"
        description="Discover powerful features of myBOQ: Dynamic login, smart duplication, PDF export, SSR-based estimates, and more."
        keywords="BOQ features, SSR estimation, civil software features"
        image="https://myboq.siliconmount.com/ssrlogo3.png"
        url="https://myboq.siliconmount.com/features"
      />

      {/* Feature Highlights */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-12">Feature Highlights</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {visualFeatures.slice(0, visibleCount).map((feature, index) => (
              <div
                key={index}
                onClick={() => setSelectedFeature(feature)}
                className="cursor-pointer bg-white p-4 rounded-lg shadow hover:-translate-y-1 transition"
              >
                {feature.img && <img src={feature.img} alt={feature.title} className="w-full rounded mb-3" />}
                {feature.desc && <div className="text-center font-medium text-sm text-gray-600 mb-2">{feature.desc}</div>}
                <div className="text-center font-semibold">{feature.title}</div>
              </div>
            ))}
          </div>

          {visibleCount < visualFeatures.length && (
            <div className="flex justify-center mt-10">
              <button
                onClick={handleShowMore}
                className="text-orange-600 hover:text-white hover:bg-orange-600 border-2 border-orange-500 px-6 py-2 rounded transition font-semibold"
              >
                Show More
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Modal with Arrow Navigation */}
      {selectedFeature && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center px-4">
          <div className="bg-white rounded-lg w-[80vw] h-[80vh] p-6 relative overflow-auto shadow-xl">

            {/* Close Button */}
            <button
              onClick={() => setSelectedFeature(null)}
              className="absolute top-3 right-3 text-gray-600 hover:text-red-600 text-2xl font-bold"
            >
              &times;
            </button>

            {/* Left Arrow */}
            {showPrev && (
              <button
                className="absolute left-3 top-1/2 transform -translate-y-1/2 bg-gray-200 hover:bg-gray-300 rounded-full p-2 shadow"
                onClick={() => setSelectedFeature(visualFeatures[currentIndex - 1])}
              >
                ◀
              </button>
            )}

            {/* Right Arrow */}
            {showNext && (
              <button
                className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-gray-200 hover:bg-gray-300 rounded-full p-2 shadow"
                onClick={() => setSelectedFeature(visualFeatures[currentIndex + 1])}
              >
                ▶
              </button>
            )}

            {/* Content */}
            <div className="flex flex-col items-center justify-center h-full text-center px-4">
              {selectedFeature.img && (
                <img src={selectedFeature.img} alt={selectedFeature.title} className="max-h-60 mb-6 rounded shadow" />
              )}
              <h3 className="text-2xl font-bold mb-2 text-gray-800">{selectedFeature.title}</h3>
              <p className="text-gray-600 text-base mb-4">
                {selectedFeature.desc || 'No description available.'}
              </p>
              {selectedFeature.button && (
                <button
                  onClick={() => {
                    setSelectedFeature(null);
                    navigate('/signup');
                  }}
                  className="mt-4 bg-orange-500 text-white px-4 py-2 rounded hover:bg-white hover:text-black border-2 border-orange-500 transition"
                >
                  Get Started
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Full Feature Suite */}
      <section className="py-20 px-6 lg:px-20 bg-gradient-to-b from-white via-gray-50 to-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-20 text-gray-800">Full Feature Suite</h2>
          <div className="relative border-l-4 border-orange-500 pl-8 space-y-14">
            {detailedSections.map((section, idx) => (
              <div key={idx} className="relative">
                <div className="absolute -left-5 top-1 w-10 h-10 flex items-center justify-center bg-orange-500 text-white font-bold rounded-full shadow-md">
                  {idx + 1}
                </div>
                <div className="bg-white p-6 rounded-xl shadow-md border border-gray-200">
                  <h3 className="text-2xl font-semibold text-orange-600 mb-4">{section.title}</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700 text-base">
                    {section.points.map((point, i) => (
                      <li key={i}>{point}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Subscription Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-gray-800 mb-12">
            Why Subscription is Better than One-Time Payments
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
            <div className="bg-orange-50 p-6 rounded-lg shadow">
              <h3 className="text-2xl font-semibold text-orange-600 mb-4">One-Time or Annual Fees</h3>
              <ul className="list-disc list-inside space-y-3 text-gray-700">
                <li>⛔ High upfront cost with uncertain value</li>
                <li>⛔ Requires payment again for major updates</li>
                <li>⛔ No usage flexibility – pay whether you use it or not</li>
                <li>⛔ Limited support or access after a year</li>
              </ul>
            </div>
            <div className="bg-green-50 p-6 rounded-lg shadow relative">
              {SHOW_FREE_MONTH && (
                <div className="absolute top-4 right-4 bg-green-600 text-white px-3 py-1 text-sm rounded-full shadow">
                  🎉 1 Month FREE!
                </div>
              )}
              <h3 className="text-2xl font-semibold text-green-600 mb-4">Credit-Based Subscription</h3>
              <ul className="list-disc list-inside space-y-3 text-gray-700">
                {SHOW_FREE_MONTH && <li>✅ Get started with a full month free — no credit card required</li>}
                <li>✅ Only pay when you create an estimate</li>
                <li>✅ Always get the latest features & updates</li>
                <li>✅ Choose plans based on your usage</li>
                <li>✅ Option to top up or switch anytime</li>
                <li>✅ Priority support and exclusive offers</li>
              </ul>
            </div>
          </div>
          <div className="mt-10">
            <button
              onClick={() => navigate('/pricing')}
              className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition"
            >
              View Subscription Plans
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Features;
